import { apiBase } from "../data/app";
import { standalone } from "./install";

/**
 * Register the PWA service worker (public/service-worker.js). Production
 * only — CRA's dev server doesn't serve the file at the SW scope, and a
 * caching worker during development hides live changes.
 *
 * The API's base URL and the image cache origin ride along as query params
 * so the worker (which can't read process.env) knows which requests to
 * apply the network-first API and cache-first cover strategies to. The base
 * keeps its path: a preview's API is /api on its own origin.
 */
export function register(): void {
  if (process.env.NODE_ENV !== "production") return;
  if (!("serviceWorker" in navigator)) return;

  window.addEventListener("load", () => {
    const params = new URLSearchParams({ api: apiBase.replace(/\/+$/, "") });
    const imageUrl = process.env.REACT_APP_IMAGE_URL;
    if (imageUrl) params.set("img", new URL(imageUrl).origin);
    navigator.serviceWorker
      .register(`/service-worker.js?${params}`)
      .then(keepStorage)
      .catch(() => {
        // Registration failure (private mode, unsupported) degrades to a
        // plain web app — nothing to surface to the user.
      });
  });
}

/**
 * Ask the browser to keep the worker's caches under storage pressure (ADR
 * 0027, step 7): without it, iOS may evict the stored shell, and the
 * installed app then can't open offline. Only once installed, where the app
 * is meant to work offline; a browser tab isn't asked, so Firefox never
 * prompts for it.
 */
function keepStorage(): void {
  if (!standalone() || !navigator.storage?.persist) return;
  navigator.storage.persist().catch(() => {});
}

/**
 * Delete the worker's cached API responses (the last-seen library, notes
 * included) when a session ends. The worker also keys them by session, so
 * this is about not leaving them on the device. Matches every version of
 * the cache's name.
 */
export function clearApiCache(): void {
  if (typeof caches === "undefined") return;
  caches
    .keys()
    .then((keys) =>
      Promise.all(
        keys
          .filter((key) => key.startsWith("khipur-api-"))
          .map((key) => caches.delete(key)),
      ),
    )
    .catch(() => {});
}
